/**
 * Malayalam Calendar (Kollavarsham) Utility Functions
 *
 * The Malayalam calendar is a solar calendar used in Kerala, India.
 * It follows the sidereal year and has 12 months aligned with zodiac signs.
 * The Kollavarsham era started in 825 CE.
 */

// Malayalam month names in native script
export const malayalamMonths = [
  { name: 'ചിങ്ങം', transliteration: 'Chingam' },      // Aug 17 - Sep 16 (31 days)
  { name: 'കന്നി', transliteration: 'Kanni' },         // Sep 17 - Oct 17 (31 days)
  { name: 'തുലാം', transliteration: 'Thulam' },        // Oct 18 - Nov 16 (30 days)
  { name: 'വൃശ്ചികം', transliteration: 'Vrischikam' },  // Nov 17 - Dec 16 (30 days)
  { name: 'ധനു', transliteration: 'Dhanu' },           // Dec 17 - Jan 14 (29 days)
  { name: 'മകരം', transliteration: 'Makaram' },        // Jan 15 - Feb 12 (29 days)
  { name: 'കുംഭം', transliteration: 'Kumbham' },       // Feb 13 - Mar 13 (29-30 days)
  { name: 'മീനം', transliteration: 'Meenam' },         // Mar 14 - Apr 13 (31 days)
  { name: 'മേടം', transliteration: 'Medam' },          // Apr 14 - May 14 (31 days)
  { name: 'ഇടവം', transliteration: 'Edavam' },         // May 15 - Jun 14 (31 days)
  { name: 'മിഥുനം', transliteration: 'Mithunam' },     // Jun 15 - Jul 15 (31 days)
  { name: 'കർക്കടകം', transliteration: 'Karkidakam' } // Jul 16 - Aug 16 (32 days)
];

// Malayalam month start dates (approx. same each year in Gregorian calendar)
// Each month is approximately 30-31 days based on solar transit (Sankranti)
// [month (0-11), date]
const monthStartDates = [
  { month: 7, date: 17 },   // Chingam (Leo): Aug 17 - Sep 16 (31 days)
  { month: 8, date: 17 },   // Kanni (Virgo): Sep 17 - Oct 17 (31 days)
  { month: 9, date: 18 },   // Thulam (Libra): Oct 18 - Nov 16 (30 days)
  { month: 10, date: 17 },  // Vrischikam (Scorpio): Nov 17 - Dec 16 (30 days)
  { month: 11, date: 17 },  // Dhanu (Sagittarius): Dec 17 - Jan 14 (29 days)
  { month: 0, date: 15 },   // Makaram (Capricorn): Jan 15 - Feb 12 (29 days)
  { month: 1, date: 13 },   // Kumbham (Aquarius): Feb 13 - Mar 13 (29-30 days)
  { month: 2, date: 14 },   // Meenam (Pisces): Mar 14 - Apr 13 (31 days)
  { month: 3, date: 14 },   // Medam (Aries): Apr 14 - May 14 (31 days)
  { month: 4, date: 15 },   // Edavam (Taurus): May 15 - Jun 14 (31 days)
  { month: 5, date: 15 },   // Mithunam (Gemini): Jun 15 - Jul 15 (31 days)
  { month: 6, date: 16 }    // Karkidakam (Cancer): Jul 16 - Aug 16 (32 days)
];

/**
 * Convert Gregorian date to Malayalam calendar date
 * @param {Date} gregorianDate - JavaScript Date object
 * @returns {Object} - { monthIndex, monthName, date, year }
 */
export const gregorianToMalayalam = (gregorianDate) => {
  const year = gregorianDate.getFullYear();
  const month = gregorianDate.getMonth();
  const date = gregorianDate.getDate();

  // Find which Malayalam month the date falls into
  let malayalamMonthIndex = -1;
  let malayalamDate = 1;

  // Check each Malayalam month
  for (let i = 0; i < monthStartDates.length; i++) {
    const currentMonthStart = monthStartDates[i];
    const nextMonthStart = monthStartDates[(i + 1) % 12];

    // Create Date objects for start of current and next Malayalam month
    let currentStartDate = new Date(year, currentMonthStart.month, currentMonthStart.date);
    let nextStartDate = new Date(year, nextMonthStart.month, nextMonthStart.date);

    // Handle year boundaries correctly:
    // If we're in Jan-Jul (months 0-6) and checking a Malayalam month that starts in Aug-Dec (7-11),
    // the Malayalam month started in the PREVIOUS Gregorian year
    if (month < 7 && currentMonthStart.month >= 7) {
      currentStartDate.setFullYear(year - 1);
    }

    // If next month starts before current month (wraps to next year)
    if (nextMonthStart.month < currentMonthStart.month) {
      // Next month is in the next year
      nextStartDate.setFullYear(currentStartDate.getFullYear() + 1);
    } else if (month < 7 && nextMonthStart.month >= 7) {
      // Both current and next in previous year scenario
      nextStartDate.setFullYear(year - 1);
    }

    // Check if the date falls in this Malayalam month
    if (gregorianDate >= currentStartDate && gregorianDate < nextStartDate) {
      malayalamMonthIndex = i;
      // Calculate Malayalam date (days since month start + 1)
      malayalamDate = Math.floor((gregorianDate - currentStartDate) / (1000 * 60 * 60 * 24)) + 1;
      break;
    }
  }

  // If month not found, default to first month
  if (malayalamMonthIndex === -1) {
    malayalamMonthIndex = 0;
    malayalamDate = 1;
  }

  // Calculate Malayalam year (Kollavarsham era started in 825 CE)
  // Malayalam year changes with Chingam (month index 0) in August-September
  let malayalamYear = year - 824;

  // If we're before Chingam month (months 5-11 in our array which are Makaram to Karkidakam)
  // we're still in the previous Malayalam year
  if (malayalamMonthIndex >= 5) {
    malayalamYear = year - 825;
  }

  return {
    monthIndex: malayalamMonthIndex,
    monthName: malayalamMonths[malayalamMonthIndex].name,
    transliteration: malayalamMonths[malayalamMonthIndex].transliteration,
    date: malayalamDate,
    year: malayalamYear
  };
};

/**
 * Get Malayalam weekday names
 * @returns {Array} - Array of Malayalam weekday names starting from Sunday
 */
export const getMalayalamWeekdays = () => {
  return [
    'ഞായർ',      // Sunday
    'തിങ്കൾ',     // Monday
    'ചൊവ്വ',      // Tuesday
    'ബുധൻ',       // Wednesday
    'വ്യാഴം',      // Thursday
    'വെള്ളി',     // Friday
    'ശനി'        // Saturday
  ];
};

/**
 * Get all days in a Malayalam month for display in calendar
 * @param {Date} gregorianDate - Current date to determine which Malayalam month
 * @returns {Array} - Array of day objects with Malayalam and Gregorian info
 */
export const getMalayalamMonthDays = (gregorianDate) => {
  const currentMalayalamDate = gregorianToMalayalam(gregorianDate);
  const monthIndex = currentMalayalamDate.monthIndex;

  // Get start date of current Malayalam month
  const startDateInfo = monthStartDates[monthIndex];
  const year = gregorianDate.getFullYear();

  // Adjust year for months that fall in next calendar year
  const adjustedYear = startDateInfo.month < 7 && gregorianDate.getMonth() >= 7
    ? year + 1
    : year;

  const monthStartDate = new Date(adjustedYear, startDateInfo.month, startDateInfo.date);

  // Get start date of next Malayalam month
  const nextMonthIndex = (monthIndex + 1) % 12;
  const nextStartDateInfo = monthStartDates[nextMonthIndex];
  const nextMonthYear = nextStartDateInfo.month < startDateInfo.month
    ? adjustedYear + 1
    : adjustedYear;
  const nextMonthStartDate = new Date(nextMonthYear, nextStartDateInfo.month, nextStartDateInfo.date);

  // Calculate number of days in Malayalam month
  const daysInMonth = Math.floor((nextMonthStartDate - monthStartDate) / (1000 * 60 * 60 * 24));

  // Get starting day of week for Malayalam month
  const startingDayOfWeek = monthStartDate.getDay();

  const days = [];

  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }

  // Add all days of the Malayalam month
  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(monthStartDate);
    currentDate.setDate(monthStartDate.getDate() + day - 1);

    days.push({
      malayalamDate: day,
      gregorianDate: currentDate.getDate(),
      gregorianMonth: currentDate.getMonth(),
      gregorianYear: currentDate.getFullYear(),
      fullDate: currentDate
    });
  }

  return days;
};
